/**
 * @file RevealIssueGroup.tsx
 * @description Неудачное «Сохранить блок» (замечания проверки) — раскрыть
 * первую на экране группу с ошибкой, прокрутить к ней и дать фокус первому
 * полю с ошибкой (phase19.16, SB122; ревью партии SB122). Ставится внутрь
 * `SettingsGroups` вкладки или панели элемента; запрос (`reveal`) и его
 * сброс — из формы секции. Групп с ошибкой здесь нет — запрос сбрасывается
 * сразу, чтобы не сработать при следующем открытии панели.
 *
 * SB120 этап 1 (§7.19 фазы, «а где исправлять?»): переход из списка
 * замечаний (`revealPath`) раскрывает группу именно этого поля, а не первую.
 *
 * ```tsx
 * <SettingsGroups panel="section-look"><RevealIssueGroup rules={LOOK_ISSUE_RULES} />…</SettingsGroups>
 * ```
 */

import { useEffect } from "react";

import { useOpenGroupRequest } from "../../ui/settings-groups";
import { useSectionForm } from "./section-form-context";
import { useSectionIssueGroups } from "../../../hooks/site-builder/useSectionIssueGroups";
import { firstIssueGroup, issueGroupOf, type IssueGroupRule } from "../../../lib/site-builder/issue-groups";

interface Props<G extends string> {
  rules: readonly IssueGroupRule<G>[];
  /** Путь элемента (панель элемента). */
  base?: string;
  /** Порядок групп на экране; нет — порядок правил. */
  order?: readonly G[];
}

export function RevealIssueGroup<G extends string>({ rules, base = "", order }: Props<G>) {
  const { reveal, revealDone, revealPath } = useSectionForm();
  const found = useSectionIssueGroups(rules, base);
  const target = revealPath !== null ? issueGroupOf(revealPath, rules, base) : null;
  const first = reveal === null ? null : (target ?? firstIssueGroup(found, order ?? rules.map((r) => r.group)));
  useOpenGroupRequest(first, reveal, { focusInvalid: true, onDone: revealDone });
  useEffect(() => {
    if (reveal !== null && first === null) revealDone();
  }, [reveal, first, revealDone]);
  return null;
}
